// src/adhkar/AdhkarPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import "./adhkar.css";
import {
  ADHKAR_NOTE_EN,
  ADHKAR_NOTE_FR,
  ADHKAR_SOURCE_EN,
  ADHKAR_SOURCE_FR,
  MORNING_ADHKAR,
  EVENING_ADHKAR,
} from "./adhkarData";

const LS_KEYS = {
  morningDone: "ADHKAR_MORNING_DONE_V1",
  eveningDone: "ADHKAR_EVENING_DONE_V1",
  morningCounts: "ADHKAR_MORNING_COUNTS_V1",
  eveningCounts: "ADHKAR_EVENING_COUNTS_V1",
  lastResetDate: "ADHKAR_LAST_RESET_DATE_V1",
  arScale: "ADHKAR_AR_SCALE_V1",
};

const getLocalDateKey = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const safeJsonParse = (raw, fallback) => {
  try {
    if (!raw) return fallback;
    const v = JSON.parse(raw);
    return v ?? fallback;
  } catch {
    return fallback;
  }
};

export default function AdhkarPage({ view, language, goTo }) {
  // view = "adhkar_choice" | "adhkar_morning" | "adhkar_evening"
  const isChoice = view === "adhkar_choice";
  const isMorning = view === "adhkar_morning";
  const isEvening = view === "adhkar_evening";

  const list = useMemo(() => {
    if (isMorning) return MORNING_ADHKAR;
    if (isEvening) return EVENING_ADHKAR;
    return [];
  }, [isMorning, isEvening]);

  // sous-vue interne
  const [subView, setSubView] = useState("list"); // "list" | "read"
  const [readId, setReadId] = useState(null);

  // ✅ done + counts séparés
  const [morningDone, setMorningDone] = useState(() =>
    safeJsonParse(window.localStorage.getItem(LS_KEYS.morningDone), {})
  );
  const [eveningDone, setEveningDone] = useState(() =>
    safeJsonParse(window.localStorage.getItem(LS_KEYS.eveningDone), {})
  );
  const [morningCounts, setMorningCounts] = useState(() =>
    safeJsonParse(window.localStorage.getItem(LS_KEYS.morningCounts), {})
  );
  const [eveningCounts, setEveningCounts] = useState(() =>
    safeJsonParse(window.localStorage.getItem(LS_KEYS.eveningCounts), {})
  );

  const [arScale, setArScale] = useState(() => {
    const raw = window.localStorage.getItem(LS_KEYS.arScale);
    const n = raw ? Number(raw) : 1;
    if (!Number.isFinite(n)) return 1;
    return Math.min(1.25, Math.max(0.9, n));
  });

  // ✅ reset quotidien auto
  useEffect(() => {
    const ensureReset = () => {
      try {
        const today = getLocalDateKey();
        const last = window.localStorage.getItem(LS_KEYS.lastResetDate);
        if (last !== today) {
          setMorningDone({});
          setEveningDone({});
          setMorningCounts({});
          setEveningCounts({});
          window.localStorage.setItem(LS_KEYS.morningDone, JSON.stringify({}));
          window.localStorage.setItem(LS_KEYS.eveningDone, JSON.stringify({}));
          window.localStorage.setItem(LS_KEYS.morningCounts, JSON.stringify({}));
          window.localStorage.setItem(LS_KEYS.eveningCounts, JSON.stringify({}));
          window.localStorage.setItem(LS_KEYS.lastResetDate, today);
        }
      } catch {}
    };

    ensureReset();
    const onFocus = () => ensureReset();
    const onVis = () => {
      if (!document.hidden) ensureReset();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  // sauvegardes
  useEffect(() => {
    try {
      window.localStorage.setItem(LS_KEYS.morningDone, JSON.stringify(morningDone));
    } catch {}
  }, [morningDone]);

  useEffect(() => {
    try {
      window.localStorage.setItem(LS_KEYS.eveningDone, JSON.stringify(eveningDone));
    } catch {}
  }, [eveningDone]);

  useEffect(() => {
    try {
      window.localStorage.setItem(LS_KEYS.morningCounts, JSON.stringify(morningCounts));
    } catch {}
  }, [morningCounts]);

  useEffect(() => {
    try {
      window.localStorage.setItem(LS_KEYS.eveningCounts, JSON.stringify(eveningCounts));
    } catch {}
  }, [eveningCounts]);

  useEffect(() => {
    try {
      window.localStorage.setItem(LS_KEYS.arScale, String(arScale));
    } catch {}
  }, [arScale]);

  // quand on change matin/soir, revenir en "list"
  useEffect(() => {
    setSubView("list");
    setReadId(null);
  }, [view]);

  const doneMap = isMorning ? morningDone : eveningDone;
  const setDoneMap = isMorning ? setMorningDone : setEveningDone;

  const countsMap = isMorning ? morningCounts : eveningCounts;
  const setCountsMap = isMorning ? setMorningCounts : setEveningCounts;

  const progressDone = useMemo(() => {
    if (!list.length) return 0;
    return list.reduce((acc, it) => acc + (doneMap?.[it.id] ? 1 : 0), 0);
  }, [list, doneMap]);

  const topSource = language === "fr" ? ADHKAR_SOURCE_FR : ADHKAR_SOURCE_EN;
  const topNote = language === "fr" ? ADHKAR_NOTE_FR : ADHKAR_NOTE_EN;

  const decAr = () => setArScale((s) => Math.max(0.9, Math.round((s - 0.05) * 100) / 100));
  const incAr = () => setArScale((s) => Math.min(1.25, Math.round((s + 0.05) * 100) / 100));

  const openRead = (id) => {
    setReadId(id);
    setSubView("read");
  };

  const backToList = () => {
    setSubView("list");
    setReadId(null);
  };

  const toggleDone = (id) => {
    setDoneMap((prev) => ({ ...(prev || {}), [id]: !prev?.[id] }));
  };

  const currentItem = useMemo(() => {
    if (!readId) return null;
    return list.find((x) => x.id === readId) || null;
  }, [readId, list]);

  const titleWithX = (it) => {
    const n = Number(it?.target || 1);
    const x = Number.isFinite(n) ? n : 1;
    const base = language === "fr" ? it.titleFr : it.titleEn;
    return `${base} x ${x}`;
  };

  // compteur uniquement si target > 1
  const hasCounter = currentItem && Number(currentItem.target) > 1;
  const target = hasCounter ? Number(currentItem.target) : 1;
  const count = currentItem ? Number(countsMap?.[currentItem.id] || 0) : 0;
  const safeCount = Number.isFinite(count) ? count : 0;

  const incCount = () => {
    if (!currentItem) return;
    setCountsMap((prev) => {
      const cur = Number(prev?.[currentItem.id] || 0) || 0;
      const next = Math.min(target, cur + 1);
      return { ...(prev || {}), [currentItem.id]: next };
    });
  };

  const resetCount = () => {
    if (!currentItem) return;
    setCountsMap((prev) => ({ ...(prev || {}), [currentItem.id]: 0 }));
  };

  // ✅ Base commune : important pour padding + fond + éviter que tout soit trop haut
  const baseMainClass =
    "night-screen adhkar-screen " +
    (isChoice ? "adhkar-screen--choice" : subView === "list" ? "adhkar-screen--list" : "adhkar-screen--read");

  // --------------------------
  // RENDERS
  // --------------------------

  if (isChoice) {
    return (
      <main className={baseMainClass}>
        <div className="portrait-panel">
          <div className="panel-header-card adhkar-header-card">
            <h1 className="panel-title">
              {language === "fr" ? "Adhkâr du matin/du soir" : "Morning/Evening Adhkar"}
            </h1>
            <p className="panel-subtitle secondary" style={{ marginTop: "0.35rem" }}>
              {language === "fr"
                ? "Choisis ta liste. Tu pourras ensuite cliquer sur “Lire”. Cette liste n’est pas exhaustive. Tu peux bien sûr faire d’autres invocations que tu connais déjà, si tu le souhaites."
                : "Choose your list. Then tap “Read”. This list is not exhaustive. You may of course recite other supplications that you already know, if you wish."}
            </p>
          </div>

          <div className="adhkar-choice-actions">
            <button className="primary-button" onClick={() => goTo("adhkar_morning")}>
              {language === "fr" ? "☀️ Matin" : "☀️ Morning"}
            </button>
            <button className="secondary-button" onClick={() => goTo("adhkar_evening")}>
              {language === "fr" ? "🌙 Soir" : "🌙 Evening"}
            </button>
          </div>

          <button className="secondary-button back-button" onClick={() => goTo("welcome")}>
            {language === "fr" ? "Retour" : "Back"}
          </button>
        </div>
      </main>
    );
  }

  // LISTE (capture 2)
  if (subView === "list") {
    return (
      <main className={baseMainClass}>
        <div className="portrait-panel">
          <div className="panel-header-card adhkar-header-card">
            <div className="adhkar-top-row">
              <h1 className="panel-title" style={{ marginBottom: 0 }}>
                {isMorning
                  ? language === "fr"
                    ? "Adhkâr du matin"
                    : "Morning Adhkar"
                  : language === "fr"
                  ? "Adhkâr du soir"
                  : "Evening Adhkar"}
              </h1>

              <button
                type="button"
                className="header-chip header-chip--ghost"
                onClick={() => goTo("adhkar_choice")}
              >
                {language === "fr" ? "Changer" : "Change"}
              </button>
            </div>

            <p className="panel-subtitle secondary" style={{ margin: "0.35rem 0 0" }}>
              <strong>{language === "fr" ? "Progression" : "Progress"} :</strong> {progressDone}/
              {list.length}
            </p>

            <div className="adhkar-progress-bar" aria-hidden="true">
              <div
                className="adhkar-progress-fill"
                style={{
                  width: list.length ? `${Math.round((progressDone / list.length) * 100)}%` : "0%",
                }}
              />
            </div>

            <div className="adhkar-disclaimer">
              <p className="panel-subtitle secondary" style={{ margin: 0 }}>
                {topSource}
              </p>
              <p className="panel-subtitle secondary" style={{ margin: "0.35rem 0 0" }}>
                {topNote}
              </p>
            </div>
          </div>

          <div style={{ marginTop: "0.85rem" }}>
            {list.map((it) => (
              <div key={it.id} className="adhkar-track-card">
                <div className="adhkar-track-title">{titleWithX(it)}</div>

                <div className="adhkar-mini-bar">
                  <label className="adhkar-mini-check">
                    <input
                      type="checkbox"
                      checked={!!doneMap?.[it.id]}
                      onChange={() => toggleDone(it.id)}
                    />
                    <span>{language === "fr" ? "Fait" : "Done"}</span>
                  </label>

                  <button type="button" className="adhkar-mini-btn" onClick={() => openRead(it.id)}>
                    {language === "fr" ? "Lire" : "Read"}
                  </button>
                </div>
              </div>
            ))}
          </div>

          <button className="secondary-button back-button" onClick={() => goTo("adhkar_choice")}>
            {language === "fr" ? "Retour" : "Back"}
          </button>
        </div>
      </main>
    );
  }

  // LIRE (capture 3)
  return (
    <main className={baseMainClass}>
      <div className="portrait-panel">
        <div className="panel-header-card adhkar-read-header">
          <div className="adhkar-read-top">
            <button type="button" className="secondary-button back-button" onClick={backToList}>
              {language === "fr" ? "Retour aux invocations" : "Back to invocations"}
            </button>

            <div className="adhkar-read-actions">
              {currentItem ? (
                <label className="adhkar-read-check">
                  <input
                    type="checkbox"
                    checked={!!doneMap?.[currentItem.id]}
                    onChange={() => toggleDone(currentItem.id)}
                  />
                  <span>{language === "fr" ? "Fait" : "Done"}</span>
                </label>
              ) : null}

              <div className="adhkar-read-ar-controls">
                <button type="button" className="header-chip" onClick={decAr}>
                  A-
                </button>
                <button type="button" className="header-chip" onClick={incAr}>
                  A+
                </button>
              </div>
            </div>
          </div>

          <p className="panel-subtitle secondary" style={{ marginTop: "0.5rem" }}>
            {language === "fr"
              ? " La phonétique est affichée pour faciliter la lecture."
              : " Transliteration is displayed to help you recite."}
          </p>
        </div>

        <div className="adhkar-read-card">
          {currentItem ? (
            <>
             {hasCounter ? (
  <div className="adhkar-counter-box">
    <div className="adhkar-counter-row">
      <span className="adhkar-counter-progress">
        {safeCount}/{target}
      </span>

      <button
        type="button"
        className="adhkar-counter-bigTap"
        onClick={incCount}
        disabled={safeCount >= target}
        aria-label={language === "fr" ? "Ajouter un compte" : "Add one count"}
      >
        +
      </button>

      <button
        type="button"
        className="adhkar-counter-reset"
        onClick={(e) => { e.stopPropagation(); resetCount(); }}
      >
        Reset
      </button>
    </div>
  </div>
) : null}


              {/* ARABE */}
              <p
                className="adhkar-ar"
                dir="rtl"
                lang="ar"
                style={{ fontSize: `${1.38 * arScale}rem` }}
              >
                {currentItem.ar}
              </p>

              {/* PHONETIQUE TOUJOURS */}
              <div style={{ marginTop: "1rem" }}>
                <p className="adhkar-label">{language === "fr" ? "Phonétique" : "Transliteration"}</p>
                <p className="adhkar-translit">{currentItem.translit}</p>
              </div>

              {/* TRAD */}
              <div style={{ marginTop: "1rem" }}>
                <p className="adhkar-label">{language === "fr" ? "FR" : "EN"}</p>
                <p className="adhkar-translation">
                  {language === "fr" ? currentItem.fr : currentItem.en}
                </p>
              </div>

              {/* SOURCE (discret) */}
              {currentItem.source ? (
                <p className="adhkar-source">
                  <strong>{language === "fr" ? "Source" : "Source"} :</strong> {currentItem.source}
                </p>
              ) : null}
            </>
          ) : (
            <p className="panel-subtitle secondary" style={{ margin: 0 }}>
              {language === "fr" ? "Invocation introuvable." : "Adhkar not found."}
            </p>
          )}
        </div>
      </div>
    </main>
  );
}
